// Ubicación en el repo: src/pages/Exams.jsx
// Listado de Audiometrías — Fase 1 del módulo (ver DECISIONS.md): CRUD
// básico. Mismo patrón que Patients.jsx: tabla + búsqueda + filtro por
// sucursal activa (RLS ya restringe por patient_branch_match, esto es
// solo para acotar a la sucursal elegida en el header) + crear/editar vía
// modal + soft-delete.
import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { listExams, softDeleteExam, getExamById } from '../lib/exams';
import { listActiveProfessionals } from '../lib/professionals';
import { isModuleEnabledForBranch } from '../lib/branches';
import { formatPatientName, formatProfessionalName, formatDate } from '../lib/format';
import ExamFormModal from '../components/ExamFormModal';
import ExamDetailModal from '../components/ExamDetailModal';
import CompareExamsModal from '../components/CompareExamsModal';
import Spinner from '../components/Spinner';

export default function Exams() {
  const { activeBranchId, activeBranch, professional } = useOutletContext();

  const [search, setSearch] = useState('');
  const [exams, setExams] = useState([]);
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  const [detailTarget, setDetailTarget] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Selección para "Comparar seleccionadas" (spec: hasta 2 audiometrías,
  // idealmente del mismo paciente) — mismo criterio que compareSelection
  // del prototipo: al elegir una tercera, se descarta la más vieja.
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareTargets, setCompareTargets] = useState(null); // [examA, examB] con od/oi completos
  const [compareLoading, setCompareLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError('');
    try {
      const data = await listExams({ branchId: activeBranchId, search });
      setExams(data);
    } catch (err) {
      setError(err.message || 'No se pudo cargar el listado de audiometrías.');
    } finally {
      setLoading(false);
    }
  }, [activeBranchId, search]);

  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
  }, [refresh]);

  useEffect(() => {
    listActiveProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  function handleNewExam() {
    setEditTarget(null);
    setFormOpen(true);
  }

  // La fila del listado no trae los campos clínicos (od/oi/bone_od/...) —
  // solo lo necesario para mostrar la tabla. Para editar o ver el detalle
  // se pide la ficha completa, y se le agregan los datos de
  // paciente/profesional ya embebidos en la fila del listado (evita una
  // segunda consulta para eso).
  async function loadFullExam(exam) {
    const full = await getExamById(exam.id);
    return { ...full, patients: exam.patients, professionals: exam.professionals };
  }

  async function handleEditClick(exam) {
    setEditLoading(true);
    setError('');
    try {
      setEditTarget(await loadFullExam(exam));
      setFormOpen(true);
    } catch (err) {
      setError(err.message || 'No se pudo cargar la audiometría.');
    } finally {
      setEditLoading(false);
    }
  }

  async function handleViewClick(exam) {
    setDetailLoading(true);
    setError('');
    try {
      setDetailTarget(await loadFullExam(exam));
    } catch (err) {
      setError(err.message || 'No se pudo cargar la audiometría.');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleEditFromDetail() {
    setEditTarget(detailTarget);
    setDetailTarget(null);
    setFormOpen(true);
  }

  async function handleDeleteClick(exam) {
    const ok = window.confirm(
      `¿Eliminar la audiometría de ${formatPatientName(exam.patients)} del ${formatDate(exam.date)}?`
    );
    if (!ok) return;
    try {
      await softDeleteExam(exam.id, professional.id);
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar la audiometría.');
    }
  }

  function handleFormSaved() {
    setFormOpen(false);
    setEditTarget(null);
    refresh();
  }

  function toggleCompareSelect(exam, checked) {
    setCompareSelection((sel) => {
      if (!checked) return sel.filter((e) => e.id !== exam.id);
      const next = [...sel, exam];
      return next.length > 2 ? next.slice(next.length - 2) : next;
    });
  }

  async function handleOpenCompare() {
    if (compareSelection.length !== 2) return;
    setCompareLoading(true);
    setError('');
    try {
      const [a, b] = await Promise.all(compareSelection.map(loadFullExam));
      setCompareTargets([a, b]);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las audiometrías a comparar.');
    } finally {
      setCompareLoading(false);
    }
  }

  // Guard de UX (DECISIONS.md #42) — el toggle ORL/OFT del header ya evita
  // llegar acá con un módulo deshabilitado para la sucursal activa, pero
  // esto cubre el caso de entrar directo por URL o quedar en esta pantalla
  // justo cuando se cambia de sucursal. La protección real sigue siendo
  // RLS (has_branch_access), esto es solo para no confundir al usuario.
  if (activeBranch && !isModuleEnabledForBranch(activeBranch, 'ORL')) {
    return (
      <div className="patients-page">
        <h1>Audiometrías</h1>
        <p className="text-muted">El módulo ORL está deshabilitado para la sucursal activa ({activeBranch.name}).</p>
      </div>
    );
  }

  return (
    <div className="patients-page">
      <div className="patients-page__toolbar">
        <h1>Audiometrías</h1>
        <div className="patients-page__toolbar-right">
          <input
            type="text"
            className="search-input"
            placeholder="Buscar por paciente o RUT…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="button" className="button-primary" onClick={handleNewExam}>
            + Nueva audiometría
          </button>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}

      <div className="compare-bar">
        <span className="field-hint">
          Selecciona 2 audiometrías (idealmente del mismo paciente) para comparar sus curvas — llevas{' '}
          {compareSelection.length}/2
        </span>
        <button
          type="button"
          className={compareSelection.length === 2 ? 'button-primary' : 'button-secondary'}
          disabled={compareSelection.length !== 2 || compareLoading}
          onClick={handleOpenCompare}
        >
          {compareLoading ? 'Cargando…' : '📊 Comparar seleccionadas'}
        </button>
      </div>

      <table className="patients-table">
        <thead>
          <tr>
            <th></th>
            <th>Paciente</th>
            <th>Fecha</th>
            <th>Profesional</th>
            <th>Equipo</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={6}><Spinner inline /></td>
            </tr>
          ) : exams.length === 0 ? (
            <tr>
              <td colSpan={6}>No hay audiometrías registradas.</td>
            </tr>
          ) : (
            exams.map((e) => (
              <tr key={e.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={compareSelection.some((s) => s.id === e.id)}
                    onChange={(ev) => toggleCompareSelect(e, ev.target.checked)}
                  />
                </td>
                <td>
                  <div>{formatPatientName(e.patients)}</div>
                  <div className="text-muted">{e.patients?.rut || 'Sin RUT'}</div>
                </td>
                <td>{formatDate(e.date)}</td>
                <td>{formatProfessionalName(e.professionals, { withRole: true })}</td>
                <td>{e.equipment || '—'}</td>
                <td className="actions-cell">
                  <button
                    type="button"
                    className="link-button"
                    disabled={detailLoading}
                    onClick={() => handleViewClick(e)}
                  >
                    Ver audiograma
                  </button>
                  <button
                    type="button"
                    className="link-button"
                    disabled={editLoading}
                    onClick={() => handleEditClick(e)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="link-button link-button--danger"
                    onClick={() => handleDeleteClick(e)}
                  >
                    Eliminar
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {formOpen && (
        <ExamFormModal
          exam={editTarget}
          branchId={activeBranchId}
          professionals={professionals}
          actorId={professional.id}
          onClose={() => {
            setFormOpen(false);
            setEditTarget(null);
          }}
          onSaved={handleFormSaved}
        />
      )}

      {detailTarget && (
        <ExamDetailModal
          exam={detailTarget}
          activeBranchId={activeBranchId}
          onClose={() => setDetailTarget(null)}
          onEdit={handleEditFromDetail}
        />
      )}

      {compareTargets && (
        <CompareExamsModal
          examA={compareTargets[0]}
          examB={compareTargets[1]}
          onClose={() => {
            setCompareTargets(null);
            setCompareSelection([]);
          }}
        />
      )}
    </div>
  );
}
